package floris.graphics.backoffice.pages.admin.techstacks.manage

import floris.graphics.models.domain.models.Techstack
import floris.graphics.models.dto.requests.techstack.InsertTechstack
import floris.graphics.models.dto.requests.techstack.UpdateTechstack
import floris.graphics.repo.TechstackRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers.Default
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.w3c.dom.url.URL
import org.w3c.files.File

class ManageTechstackViewModel(
    private val techstackId: String? = null,
    private val techstackRepository: TechstackRepo = TechstackRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Default)
) {
    private val _state = MutableStateFlow(ManageTechstackState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<ManageTechstackUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    fun onEvent(event: ManageTechstackEvent) = coroutineScope.launch {
        when (event) {
            is ManageTechstackEvent.LoadManageTechstack -> getTechstack()
            is ManageTechstackEvent.Save -> saveTechstack()
            is ManageTechstackEvent.Cancel -> _eventFlow.send(ManageTechstackUiEvent.NavigateBack)
            is ManageTechstackEvent.Delete -> deleteTechstack()
            is ManageTechstackEvent.UpdateName -> _state.update { it.copy(name = event.name) }
            is ManageTechstackEvent.UpdateLogo -> {
                if (techstackId == null) {
                    // If we are creating a new techstack, we can just update the state with the logo
                    _state.update {
                        it.copy(
                            logoFile = event.logo,
                            logoUrl = event.logo.let { URL.createObjectURL(it) })
                    }
                } else {
                    updateImageRemotely(event.logo)
                }
            }
        }
    }

    init {
        onEvent(ManageTechstackEvent.LoadManageTechstack)
    }

    private suspend fun updateImageRemotely(logo: File) {
        if (techstackId.isNullOrBlank()) return

        _state.update { it.copy(uploadingLogo = true) }
        val updatedTechstack = techstackRepository.updateTechstackImage(techstackId, logo)
        if (updatedTechstack != null) {
            _state.update { it.copy(originalTechstack = updatedTechstack, logoFile = null, uploadingLogo = false) }
        } else {
            _state.update { it.copy(uploadingLogo = false) }
            _eventFlow.send(ManageTechstackUiEvent.ShowError)
        }
    }

    private suspend fun getTechstack() {
        if (techstackId.isNullOrBlank()) return

        val techstack = techstackRepository.getTechstack(techstackId) ?: Techstack()
        _state.value = ManageTechstackState(
            originalTechstack = techstack,
            name = techstack.name,
            parentTechstackId = techstack.techstack?.id
        )
    }

    private suspend fun saveTechstack() {
        val state = _state.value

        val result = if (techstackId.isNullOrBlank()) {
            createTechstack(state)
        } else {
            updateTechstack(state)
        }

        if (result != null) {
            _eventFlow.send(ManageTechstackUiEvent.NavigateBack)
        } else {
            _eventFlow.send(ManageTechstackUiEvent.ShowError)
        }
    }

    private suspend fun createTechstack(state: ManageTechstackState): Techstack? {
        val insertTechstack = InsertTechstack(
            name = state.name.trim(),
            parentTechstackId = state.parentTechstackId
        )

        return techstackRepository.insertTechstack(insertTechstack, state.logoFile)
    }

    private suspend fun updateTechstack(state: ManageTechstackState): Techstack? {
        if (techstackId.isNullOrBlank()) return null

        val updateTechstack = UpdateTechstack(
            name = state.name.trim(),
            parentTechstackId = state.parentTechstackId,
        )

        return techstackRepository.updateTechstack(techstackId, updateTechstack)
    }

    private suspend fun deleteTechstack() {
        if (techstackId.isNullOrBlank()) return

        techstackRepository.deleteTechstack(techstackId)
        _eventFlow.send(ManageTechstackUiEvent.NavigateBack)
    }
}

data class ManageTechstackState(
    val originalTechstack: Techstack = Techstack(),
    val logoFile: File? = null,
    val logoUrl: String? = null,
    val uploadingLogo: Boolean = false,
    val name: String = "",
    val parentTechstackId: String? = null,
    val errorText: String = ""
) {
    private val nameChanged = name != originalTechstack.name
    private val logoChanged = logoFile != null

    val isCreationFlow: Boolean
        get() = originalTechstack.id.isBlank()

    val hasChanged: Boolean
        get() = nameChanged || logoChanged
}

sealed class ManageTechstackEvent {
    data object LoadManageTechstack : ManageTechstackEvent()
    data class UpdateName(val name: String) : ManageTechstackEvent()
    data class UpdateLogo(val logo: File) : ManageTechstackEvent()
    data object Save : ManageTechstackEvent()
    data object Cancel : ManageTechstackEvent()
    data object Delete : ManageTechstackEvent()
}

sealed class ManageTechstackUiEvent {
    data object ShowError : ManageTechstackUiEvent()
    data object NavigateBack : ManageTechstackUiEvent()
}
